import {
  createMeCheckoutPaymentIntentV1,
  getMeCheckoutActivePaymentsV1,
  getMeCheckoutConfigV1,
  getMeCheckoutPaymentV1,
} from '../../api/me-—-checkout/me-—-checkout';
import {
  ActiveCheckoutPaymentDto,
  CartShopDto,
  CheckoutConfigDto,
  CheckoutPaymentDto,
  CheckoutPaymentIntentDto,
  CreateCheckoutPaymentIntentRequest,
} from '../../api/model';
import { extractErrorCode, extractRepricedCart } from '@/lib/core/shop/cartErrors';

/**
 * What a payment is for. Mirrors `PaymentContexts` in `Amorim.Shared/Constants/PaymentConstants.cs`
 * — each value has exactly one server-side handler that resolves the amount and completes the
 * purchase after the money lands.
 */
export const CheckoutContexts = {
  Shop: 'shop',
  MewsBooking: 'mews_booking',
  Event: 'event',
  Ride: 'ride',
} as const;

/**
 * Method chosen by the customer. Mirrors `PaymentMethodTypes`.
 *
 * Apple Pay and Google Pay are card payment methods to Stripe, so they are **not** separate values
 * — a wallet payment sends `Card` and differs only in how the PaymentMethod is collected.
 */
export const CheckoutPaymentMethodTypes = {
  Card: 'card',
  MbWay: 'mb_way',
} as const;

/**
 * Payment lifecycle. Mirrors `PaymentStatuses`.
 *
 * Stripe delivers webhooks out of order and more than once, so the server only ever moves a payment
 * forward. Clients must treat anything outside `TERMINAL_PAYMENT_STATUSES` as "keep polling".
 */
export const CheckoutPaymentStatuses = {
  Pending: 'Pending',
  Processing: 'Processing',
  RequiresAction: 'RequiresAction',
  Succeeded: 'Succeeded',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
  Refunded: 'Refunded',
} as const;

/** Statuses a payment can no longer move out of — polling stops here. */
export const TERMINAL_PAYMENT_STATUSES: ReadonlySet<string> = new Set([
  CheckoutPaymentStatuses.Succeeded,
  CheckoutPaymentStatuses.Failed,
  CheckoutPaymentStatuses.Cancelled,
  CheckoutPaymentStatuses.Refunded,
]);

/**
 * Whether a payment has reached a state it cannot leave.
 *
 * Compared case-insensitively on purpose. The backend's own status sets are built with
 * `StringComparer.OrdinalIgnoreCase` (`PaymentConstants.cs`), so it does not treat the casing as
 * load-bearing either — and an exact-match miss here is invisible and expensive: the screen would
 * poll a finished payment forever while the customer watches a spinner.
 */
export function isTerminalPaymentStatus(status?: string | null): boolean {
  if (!status) return false;
  const normalised = status.toLowerCase();
  for (const candidate of TERMINAL_PAYMENT_STATUSES) {
    if (candidate.toLowerCase() === normalised) return true;
  }
  return false;
}

/** Case-insensitive equality against a known status, for the same reason. */
export function isPaymentStatus(status: string | null | undefined, expected: string): boolean {
  return !!status && status.toLowerCase() === expected.toLowerCase();
}

/** Whether the customer can still complete this payment (the `payments/active` set). */
export function isPaymentResumable(status?: string | null): boolean {
  return (
    status === CheckoutPaymentStatuses.Pending ||
    status === CheckoutPaymentStatuses.Processing ||
    status === CheckoutPaymentStatuses.RequiresAction
  );
}

/**
 * Keys and available methods for the payment screen.
 *
 * Read-only and side-effect free, so it is safe to call on mount: the card fields are
 * Stripe-hosted iframes that must be mounted well before the customer presses pay.
 *
 * `loadStripe` must be called with the `publishableKey` from **this** response. A client secret
 * only confirms against the account it belongs to, so re-keying after the intent exists means
 * re-mounting the card fields and losing whatever the customer already typed.
 *
 * @param context - One of `CheckoutContexts`.
 * @param contextReference - The aggregate being paid for. For events, the event's public guid.
 * @returns `CheckoutConfigDto` on HTTP 200.
 * @returns `undefined` on 400 (unknown context), 401, 502 (Stripe unreachable), or network error.
 */
export async function getCheckoutConfig(
  context: string,
  contextReference?: string
): Promise<CheckoutConfigDto | undefined> {
  try {
    const response = await getMeCheckoutConfigV1({ context, contextReference });

    if (response.status === 200) {
      return response.data as CheckoutConfigDto;
    }

    console.warn(`getCheckoutConfig: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getCheckoutConfig: request failed:', error);
    return undefined;
  }
}

/** The error code the shop's re-price guard answers a 409 with. Mirrors `CartErrors.PricesChanged`. */
const PRICES_CHANGED = 'prices_changed';

/** The shop's "this basket has already been charged" guard. Mirrors `PaymentErrors.ShopAlreadyPaid`. */
const SHOP_ALREADY_PAID = 'shop_already_paid';

/** Event: Full, or fewer seats than the party. Mirrors `PaymentErrors.EventSoldOut`. */
const EVENT_SOLD_OUT = 'event_sold_out';

/** Event: no longer on sale, incl. a date no longer Published. Mirrors `PaymentErrors.EventNotForSale`. */
const EVENT_NOT_FOR_SALE = 'event_not_for_sale';

/** Event: Salesforce's seats could not be read; nothing charged. Mirrors `PaymentErrors.EventAvailabilityUnavailable`. */
const EVENT_AVAILABILITY_UNAVAILABLE = 'event_availability_unavailable';

/** Event: the chosen date no longer exists in Salesforce. Mirrors `EventRegistrationErrors.OccurrenceNotFound`. */
const OCCURRENCE_NOT_FOUND = 'occurrence_not_found';

/**
 * The seats left riding on a 409 `event_sold_out` (`{ error, availableSpots }`), read from
 * Salesforce at that moment. `null` when the server could not read them or the body lacks them.
 */
function extractAvailableSpots(body: unknown): number | null {
  const value = (body as { availableSpots?: unknown } | null | undefined)?.availableSpots;
  return typeof value === 'number' ? value : null;
}

/** Why a payment intent could not be created, so the screen can say something useful. */
export type CreatePaymentIntentFailure =
  /** 409 `prices_changed` — the resolved price no longer matches what the customer was shown. */
  | 'stale'
  /**
   * 409 `shop_already_paid` — money has already been taken for this basket, whether or not the
   * purchase finished closing. Never a reason to charge again, and emphatically not a decline: the
   * screen has to point the customer at the order, not at another card.
   */
  | 'alreadyPaid'
  /** 404 — the reference does not exist or belongs to another customer. */
  | 'notFound'
  /** 404 `occurrence_not_found` — the event's chosen date no longer exists in Salesforce. */
  | 'occurrenceNotFound'
  /** 409 `event_sold_out` — Full, or fewer seats than the party; `availableSpots` says how many remain. */
  | 'eventSoldOut'
  /** 422 `event_not_for_sale` — complimentary, unpriced, or no longer Published. */
  | 'eventNotForSale'
  /** 503 `event_availability_unavailable` — seats unreadable right now; nothing was charged. */
  | 'eventAvailabilityUnavailable'
  /** 400, a 422 `flow_mismatch`, or any other 409 verdict on the purchase itself (no longer payable). */
  | 'rejected'
  /** 401 — session expired. */
  | 'unauthorized'
  /** 502/503 or a network failure — Stripe, CPI or the API is unreachable or unconfigured. */
  | 'unavailable';

export type CreatePaymentIntentResult =
  | { ok: true; intent: CheckoutPaymentIntentDto }
  | {
      ok: false;
      failure: CreatePaymentIntentFailure;
      /**
       * Only on `stale`: the basket as the server has just re-priced it, straight off the 409 body.
       * The shop puts this back into the cart so the customer is shown what actually changed
       * instead of being told, with no evidence, that something did.
       */
      repricedCart?: CartShopDto;
      /** Only on `eventSoldOut`: the seats left, `null` when the server could not read them. */
      availableSpots?: number | null;
    };

/**
 * Starts a payment and returns the client secret to confirm it with.
 *
 * The amount and the Stripe account are resolved server-side from `context` + `contextReference`
 * — the client never sends a money value and never chooses the merchant.
 *
 * Confirming does **not** complete the purchase; Stripe's webhook does. Poll {@link getPayment}
 * afterwards, the same way for every payment method.
 *
 * Unlike the other wrappers this returns a discriminated result rather than `undefined`: the
 * failure modes are distinguishable and the screen has to react differently to each — `stale` means
 * re-read the price, `alreadyPaid` means find the order, `rejected` means the purchase itself was
 * refused. The status code alone is not enough to tell them apart; see the 409 branch below.
 *
 * @param request - Context, reference, method, optional saved card, and the event booking block.
 * @param idempotencyKey - Required by the endpoint. Reuse the same key for retries of one attempt;
 *   generate a fresh one for a genuinely new attempt, or the cached original response is replayed.
 */
export async function createPaymentIntent(
  request: CreateCheckoutPaymentIntentRequest,
  idempotencyKey: string
): Promise<CreatePaymentIntentResult> {
  try {
    const response = await createMeCheckoutPaymentIntentV1(request, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });

    if (response.status === 200) {
      return { ok: true, intent: response.data as CheckoutPaymentIntentDto };
    }

    /*
     * The error code, not just the status. Several distinct server-side causes share one status —
     * 502 alone is `stripe_unavailable`, `account_not_configured` or `cpi_unavailable`, and the last
     * of those covers both a CPI outage and missing `Cpi:*` configuration. Logging only the number
     * meant every one of them looked identical from the client, and the cause had to be recovered
     * from the network tab by hand.
     */
    console.warn(
      `createPaymentIntent: ${request.context} intent refused with ${response.status}` +
        ` (${extractErrorCode(response.data) ?? 'no error code in body'})`
    );
    switch (response.status as number) {
      case 409: {
        // 409 is not one situation. The endpoint uses it for a re-price, for a basket that has
        // already been paid for, and for a verdict on the purchase itself (`event_sold_out`,
        // `mews_reservation_not_payable`) — three different things to tell the customer, so the
        // error code decides rather than the status alone.
        const code = extractErrorCode(response.data);
        if (code === PRICES_CHANGED) {
          return { ok: false, failure: 'stale', repricedCart: extractRepricedCart(response.data) };
        }
        if (code === SHOP_ALREADY_PAID) {
          return { ok: false, failure: 'alreadyPaid' };
        }
        if (code === EVENT_SOLD_OUT) {
          return { ok: false, failure: 'eventSoldOut', availableSpots: extractAvailableSpots(response.data) };
        }
        return { ok: false, failure: 'rejected' };
      }
      case 404:
        return {
          ok: false,
          failure:
            extractErrorCode(response.data) === OCCURRENCE_NOT_FOUND ? 'occurrenceNotFound' : 'notFound',
        };
      case 422:
        // `event_not_for_sale` (incl. a date no longer Published in Salesforce) or `flow_mismatch`
        // — a verdict on the purchase, never an outage.
        return {
          ok: false,
          failure: extractErrorCode(response.data) === EVENT_NOT_FOR_SALE ? 'eventNotForSale' : 'rejected',
        };
      case 503:
        if (extractErrorCode(response.data) === EVENT_AVAILABILITY_UNAVAILABLE) {
          return { ok: false, failure: 'eventAvailabilityUnavailable' };
        }
        return { ok: false, failure: 'unavailable' };
      case 400:
        return { ok: false, failure: 'rejected' };
      case 401:
        return { ok: false, failure: 'unauthorized' };
      default:
        return { ok: false, failure: 'unavailable' };
    }
  } catch (error) {
    console.warn('createPaymentIntent: request failed:', error);
    return { ok: false, failure: 'unavailable' };
  }
}

/**
 * Current state of a payment. Poll after confirming until the status is terminal; on success it
 * carries the resulting order and — for events — the `reservationId` of the registration the
 * server created.
 *
 * @param paymentId - From the create-intent response or from {@link getActivePayments}.
 * @returns `CheckoutPaymentDto` on HTTP 200.
 * @returns `undefined` on 401, 404 (not the user's payment), or network error.
 */
export async function getPayment(paymentId: string): Promise<CheckoutPaymentDto | undefined> {
  try {
    const response = await getMeCheckoutPaymentV1(paymentId);

    if (response.status === 200) {
      return response.data as CheckoutPaymentDto;
    }

    console.warn(`getPayment: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getPayment: request failed:', error);
    return undefined;
  }
}

/**
 * Payments that have not reached a final state, with the client secret to resume them.
 *
 * This is the recovery path for asynchronous methods: with MB WAY the customer leaves the app to
 * approve on their phone and can come back to a cold start with no payment id in memory. Without
 * this they would be stuck, because only one payment can be in flight per purchase.
 *
 * @returns `ActiveCheckoutPaymentDto[]` on HTTP 200 — empty when nothing is in flight.
 * @returns `undefined` on 401 or network error.
 */
export async function getActivePayments(): Promise<ActiveCheckoutPaymentDto[] | undefined> {
  try {
    const response = await getMeCheckoutActivePaymentsV1();

    if (response.status === 200) {
      return response.data as ActiveCheckoutPaymentDto[];
    }

    console.warn(`getActivePayments: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getActivePayments: request failed:', error);
    return undefined;
  }
}
